/**
 * product-metadata-save-error.ts — 商品通道行建 / 改失败（A8 §2 / §3）→ 弹窗内就近展示的错误块（设计稿 B2「保存错误」）。
 *
 * 只按 `code` / `errors[].field` / `details.conflict_field` 判，不按 message 文案匹配（dev-docs 10-API响应格式 §6）。
 * 框架通道建行的三类拒绝（A8 §2 校验②③与 access_level_id）给出运营可读的原因，其余回退服务端 message。
 */

import { AdminApiError } from '~/api/errors';

export interface ProductMetadataSaveErrorView {
  title: string;
  hint: string | null;
  /** 机读定位：`CODE · field` / `CODE · conflict_field = x` */
  code: string;
}

/** `channelName` = 当前通道短名（如「PayerMax」「Stripe」），用于把原因写到具体通道上 */
export function describeProductMetadataSaveError(
  err: unknown,
  channelName: string,
): ProductMetadataSaveErrorView {
  if (!(err instanceof AdminApiError)) {
    return {
      title: err instanceof Error ? err.message : '保存失败',
      hint: '网络或服务异常，可直接重试',
      code: 'NETWORK',
    };
  }
  if (err.code === 'RESOURCE_CONFLICT') {
    const field =
      typeof err.details?.conflict_field === 'string' ? err.details.conflict_field : null;
    if (field === 'vendor_product_id') {
      return {
        title: `同一 vendor_product_id 已有在用的 ${channelName} 行`,
        hint: '请改为编辑那一行。',
        code: 'RESOURCE_CONFLICT · conflict_field = vendor_product_id',
      };
    }
    return {
      title: err.message,
      hint: null,
      code: field ? `RESOURCE_CONFLICT · conflict_field = ${field}` : 'RESOURCE_CONFLICT',
    };
  }
  if (err.code === 'VALIDATION_FAILED') {
    const fields = (err.errors ?? []).map((e) => e.field);
    const code =
      fields.length > 0 ? `VALIDATION_FAILED · ${fields.join(' / ')}` : 'VALIDATION_FAILED';
    if (fields.includes('product_type')) {
      return { title: `${channelName} 只能建一次性商品`, hint: null, code };
    }
    if (fields.includes('stripe_price_id')) {
      return { title: `${channelName} 行不能填 Stripe Price`, hint: null, code };
    }
    if (fields.includes('access_level_id')) {
      // 同一字段有多种拒因（缺关联 / 无 IAP 存活基线 / 阅读卡必填，A8 §2），字段级原因取服务端该条 message，
      // 不按文案猜分支
      const reason = (err.errors ?? []).find((e) => e.field === 'access_level_id')?.message ?? null;
      return {
        title: `${channelName} 行的 access_level_id 未通过校验`,
        hint:
          reason ??
          '须已在「Adapty 产品关联」建立关联，STRIPE / 框架通道另须有同 access level 的 IAP 基线',
        code,
      };
    }
    return { title: err.message, hint: null, code };
  }
  return { title: err.message, hint: null, code: err.code };
}
